import { useState, useEffect, useRef } from "react";
import { Lock, Calendar, User, Clock, CheckCircle2, AlertCircle, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface DecisionCommitData {
  committed_option_id: string;
  committed_option_name: string;
  validity_days: 30 | 60 | 90;
  owner_name: string;
  review_date: string;
  excluded_options?: string[]; // Options explicitly excluded
}

interface TradeOff {
  optionName: string;
  criterion: string;
  impact: string; // e.g., "niedrigere Produkt-/Messaging-Schärfe"
}

interface ExcludedOption {
  id: string;
  name: string;
  consequence: string; // What you're giving up by not choosing this
  isDelayOption?: boolean; // True for Option C (Aufschub)
}

interface DecisionCommitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dominantOptionId: string;
  dominantOptionName: string;
  tradeOffs?: TradeOff[]; // Trade-offs user accepted during matrix evaluation
  excludedOptions?: ExcludedOption[]; // Other options that will be explicitly excluded
  onCommit: (data: DecisionCommitData) => Promise<void>;
}

export const DecisionCommitDialog = ({
  open,
  onOpenChange,
  dominantOptionId,
  dominantOptionName,
  tradeOffs = [],
  excludedOptions = [],
  onCommit,
}: DecisionCommitDialogProps) => {
  const [validityDays, setValidityDays] = useState<30 | 60 | 90>(30);
  const [ownerName, setOwnerName] = useState("");
  const [tradeOffAcknowledged, setTradeOffAcknowledged] = useState(false);
  const [exclusionsAcknowledged, setExclusionsAcknowledged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const ownerInputRef = useRef<HTMLInputElement>(null);

  // Auto-focus owner input when dialog opens
  useEffect(() => {
    if (open) {
      setTimeout(() => ownerInputRef.current?.focus(), 100);
      // Reset acknowledgments when dialog opens
      setTradeOffAcknowledged(tradeOffs.length === 0);
      setExclusionsAcknowledged(excludedOptions.length === 0);
    }
  }, [open, tradeOffs.length, excludedOptions.length]);

  // Calculate review date based on validity
  const calculateReviewDate = (days: number) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
  };

  const [reviewDate, setReviewDate] = useState(calculateReviewDate(30));

  const handleValidityChange = (value: string) => {
    const days = parseInt(value) as 30 | 60 | 90;
    setValidityDays(days);
    setReviewDate(calculateReviewDate(days));
  };

  const handleCommit = async () => {
    if (!ownerName.trim()) return;
    if (tradeOffs.length > 0 && !tradeOffAcknowledged) return;
    if (excludedOptions.length > 0 && !exclusionsAcknowledged) return;
    
    setIsSubmitting(true);
    try {
      await onCommit({
        committed_option_id: dominantOptionId,
        committed_option_name: dominantOptionName,
        validity_days: validityDays,
        owner_name: ownerName.trim(),
        review_date: reviewDate,
        excluded_options: excludedOptions.map(o => o.name),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValid = ownerName.trim().length > 0 && 
    (tradeOffs.length === 0 || tradeOffAcknowledged) &&
    (excludedOptions.length === 0 || exclusionsAcknowledged);

  // Format review date for display
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  // Separate real decisions from delay options
  const realDecisions = excludedOptions.filter(o => !o.isDelayOption);
  const delayOptions = excludedOptions.filter(o => o.isDelayOption);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="text-left">
          <DialogTitle className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-primary/10">
              <Lock className="h-4 w-4 text-primary" />
            </div>
            Entscheidung committen
          </DialogTitle>
          <DialogDescription className="pt-1">
            Diese Entscheidung wird dokumentiert und hat eine festgelegte Gültigkeit.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-5 py-2 overflow-y-auto flex-1 min-h-0">
          {/* Selected Option - renamed from "Dominante Option" to "Verbindlicher Fokus" */}
          <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
            <p className="text-[11px] text-muted-foreground font-medium uppercase tracking-wide mb-0.5">
              Verbindlicher Fokus
            </p>
            <p className="font-semibold text-primary">
              {dominantOptionName}
            </p>
          </div>

          {/* Explicit Exclusion Acknowledgment - REQUIRED for other options */}
          {excludedOptions.length > 0 && (
            <div className={cn(
              "p-3 rounded-lg border transition-colors",
              exclusionsAcknowledged 
                ? "bg-muted/50 border-border" 
                : "bg-destructive/5 border-destructive/20"
            )}>
              <div className="flex items-start gap-2 mb-2">
                {exclusionsAcknowledged ? (
                  <CheckCircle2 className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                )}
                <p className="text-sm font-medium">
                  {exclusionsAcknowledged ? "Verzicht bestätigt" : "Bewusster Verzicht"}
                </p>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Mit diesem Commit verzichtest du für {validityDays} Tage bewusst auf:
              </p>
              
              {/* Real decision options - A & B */}
              {realDecisions.length > 0 && (
                <ul className="space-y-2 mb-2">
                  {realDecisions.map((option, idx) => (
                    <li key={idx} className="text-xs flex items-start gap-1.5 p-2 bg-background rounded border border-border/50">
                      <span className="font-medium text-foreground shrink-0">{option.name}:</span>
                      <span className="text-muted-foreground">{option.consequence}</span>
                    </li>
                  ))}
                </ul>
              )}

              {/* Delay option C - visually different */}
              {delayOptions.length > 0 && (
                <ul className="space-y-2 mb-3">
                  {delayOptions.map((option, idx) => (
                    <li key={idx} className="text-xs flex items-start gap-1.5 p-2 bg-muted/30 rounded border border-dashed border-muted-foreground/30">
                      <AlertTriangle className="h-3.5 w-3.5 text-muted-foreground/60 mt-0.5 shrink-0" />
                      <div>
                        <span className="font-medium text-muted-foreground">{option.name}</span>
                        <span className="text-muted-foreground/80 italic ml-1">(Entscheidungsaufschub)</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {realDecisions.length > 0 && delayOptions.length === 0 && <div className="mb-3" />}

              <div className="flex items-start gap-2">
                <Checkbox 
                  id="exclusion-ack"
                  checked={exclusionsAcknowledged}
                  onCheckedChange={(checked) => setExclusionsAcknowledged(checked === true)}
                  className="mt-0.5"
                />
                <label 
                  htmlFor="exclusion-ack" 
                  className="text-xs cursor-pointer leading-relaxed"
                >
                  Ich verstehe, dass dieser Verzicht <strong>bewusst</strong> ist und akzeptiere die Konsequenzen.
                </label>
              </div>
            </div>
          )}

          {/* Trade-off Acknowledgment - only if there are trade-offs */}
          {tradeOffs.length > 0 && (
            <div className={cn(
              "p-3 rounded-lg border transition-colors",
              tradeOffAcknowledged 
                ? "bg-emerald-50/50 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-800" 
                : "bg-amber-50/50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-800"
            )}>
              <div className="flex items-start gap-2 mb-2">
                {tradeOffAcknowledged ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                )}
                <p className="text-sm font-medium">
                  {tradeOffAcknowledged ? "Trade-offs bestätigt" : "Akzeptierte Trade-offs"}
                </p>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Du hast bewusst akzeptiert, dass {dominantOptionName}:
              </p>
              <ul className="space-y-1.5 mb-3">
                {tradeOffs.map((tradeOff, idx) => (
                  <li key={idx} className="text-xs text-muted-foreground flex items-start gap-1.5">
                    <span className="text-muted-foreground/60">•</span>
                    <span>{tradeOff.impact}</span>
                  </li>
                ))}
              </ul>
              <div className="flex items-start gap-2">
                <Checkbox 
                  id="trade-off-ack"
                  checked={tradeOffAcknowledged}
                  onCheckedChange={(checked) => setTradeOffAcknowledged(checked === true)}
                  className="mt-0.5"
                />
                <label 
                  htmlFor="trade-off-ack" 
                  className="text-xs cursor-pointer leading-relaxed"
                >
                  Ich bestätige, dass dies <strong>bewusste</strong> Trade-offs sind, nicht übersehene Risiken.
                </label>
              </div>
            </div>
          )}

          {/* Decision Owner - renamed label */}
          <div className="space-y-1.5">
            <Label htmlFor="owner" className="flex items-center gap-1.5 text-sm">
              <User className="h-3.5 w-3.5 text-muted-foreground" />
              Entscheidungsverantwortung
            </Label>
            <Input
              ref={ownerInputRef}
              id="owner"
              placeholder="Name eingeben..."
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              className={cn(
                "transition-all",
                !ownerName.trim() && "ring-2 ring-primary/20 focus:ring-primary/40"
              )}
            />
            <p className="text-[11px] text-muted-foreground">
              Diese Person wird im Report als Verantwortliche genannt.
            </p>
          </div>

          {/* Validity Period */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5 text-sm">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              Gültigkeit
            </Label>
            <RadioGroup 
              value={validityDays.toString()} 
              onValueChange={handleValidityChange}
              className="grid grid-cols-3 gap-2"
            >
              {[30, 60, 90].map((days) => (
                <div 
                  key={days}
                  className={cn(
                    "border rounded-lg p-2.5 cursor-pointer transition-all text-center",
                    validityDays === days 
                      ? "border-primary bg-primary/5 ring-1 ring-primary/30" 
                      : "border-border hover:border-primary/40"
                  )}
                >
                  <RadioGroupItem value={days.toString()} id={`${days}days`} className="sr-only" />
                  <label htmlFor={`${days}days`} className="cursor-pointer block">
                    <span className="text-base font-semibold">{days}</span>
                    <span className="text-xs text-muted-foreground block">Tage</span>
                  </label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Review Date - calculated automatically, shown for clarity */}
          <div className="flex items-center gap-2 p-2.5 bg-muted/50 rounded-lg">
            <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
            <div className="text-sm">
              <span className="text-muted-foreground">Review am </span>
              <span className="font-medium">{formatDate(reviewDate)}</span>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2 shrink-0 pt-4 border-t">
          <Button 
            variant="ghost" 
            onClick={() => onOpenChange(false)}
            className="text-muted-foreground"
          >
            Abbrechen
          </Button>
          <Button 
            onClick={handleCommit}
            disabled={!isValid || isSubmitting}
            className="gap-2 min-w-[140px]"
          >
            <Lock className="h-4 w-4" />
            {isSubmitting ? "Wird gespeichert..." : "Committen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};